/**
 * Your name, your timezone and your picture.
 *
 * The picture is uploaded by reading the file in the browser, downscaling it on
 * a canvas and sending the result as a data URI. There is no object store in
 * this stack, and adding one so that four people can have a face would be the
 * largest change in the feature by a wide margin.
 *
 * Downscaling in the browser is what makes that defensible. A phone photo is
 * three or four megabytes and would blow the column bound; 256 pixels of JPEG
 * is around 20 KB, which is smaller than the page the picture appears on. The
 * server bound stays as the real enforcement, because a browser is not a place
 * to enforce anything, but a person never meets it.
 */
import { useEffect, useRef, useState } from 'react'
import { OUTREACH_LANGUAGES } from '@gojibasel/shared/locale'
import { useSession, useUpdateProfile } from '../lib/session'
import { Button } from './Button'
import { IconTrash } from './icons'
import { Field } from './Field'
import { Avatar } from './Identity'
import { Input, Select, Textarea } from './Input'
import { Modal } from './Modal'
import { ErrorState } from './States'
import { useToast } from './Toast'

/** 256px is twice the largest place a picture is drawn, so it stays crisp on a retina screen. */
const MAX_EDGE = 256
const JPEG_QUALITY = 0.86

export function ProfileDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useSession()
  const update = useUpdateProfile()
  const toast = useToast()
  const fileRef = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [tz, setTz] = useState('')
  // '' is nobody said, which translates nothing on Approvals.
  const [reading, setReading] = useState('')
  // One school per line, as the person would write it.
  const [schools, setSchools] = useState('')
  // `undefined` means untouched, `null` means "take it off", a string is a new one.
  const [picture, setPicture] = useState<string | null | undefined>(undefined)
  const [problem, setProblem] = useState<string | null>(null)

  // Re-seed every time it opens, so cancelling really does cancel.
  useEffect(() => {
    if (!open || !user) return
    setName(user.name ?? '')
    setTz(user.tz ?? '')
    setReading(user.readingLanguage ?? '')
    setSchools((user.schools ?? []).join('\n'))
    setPicture(undefined)
    setProblem(null)
    update.reset()
  }, [open, user?.id])

  if (!user) return null

  const shown = picture === undefined ? user.avatarUrl : picture

  const pick = async (file: File) => {
    setProblem(null)
    if (!file.type.startsWith('image/')) {
      setProblem('That file is not an image. Use a PNG, JPEG, GIF or WebP.')
      return
    }
    try {
      setPicture(await downscale(file))
    } catch {
      setProblem('That image could not be read. Try a different file.')
    }
  }

  const save = () => {
    update.mutate(
      {
        name: name.trim(),
        ...(tz.trim() === '' ? {} : { tz: tz.trim() }),
        ...(picture === undefined ? {} : { avatarUrl: picture }),
        ...(reading === (user.readingLanguage ?? '') ? {} : { readingLanguage: reading === '' ? null : reading }),
        ...(schools === (user.schools ?? []).join('\n') ? {} : { schools: schoolLines(schools) }),
      },
      {
        onSuccess: () => {
          toast.success('Saved', 'This is how you appear to everyone else on the platform.')
          onClose()
        },
      },
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Your name and picture"
      description="This is how you appear in the corner of the screen, on every card you own and beside every account you send from."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" data-primary="" onClick={save} loading={update.isPending} disabled={name.trim() === ''}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {update.error ? <ErrorState error={update.error} /> : null}
        {problem ? <p className="text-sm text-danger-text">{problem}</p> : null}

        <div className="flex items-center gap-4">
          <Avatar name={name || user.email} email={user.email} src={shown} size="lg" />
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) void pick(file)
                // Clearing the value lets the same file be chosen twice, which
                // otherwise silently does nothing and reads as a broken button.
                event.target.value = ''
              }}
            />
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
              Choose a picture
            </Button>
            {shown ? (
              <Button
                variant="danger-ghost"
                size="sm"
                leadingIcon={<IconTrash className="h-3.5 w-3.5" />}
                onClick={() => setPicture(null)}
              >
                Remove
              </Button>
            ) : null}
          </div>
        </div>

        <Field label="Your name" hint="What everyone else sees.">
          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Basel Ismail" />
        </Field>

        <Field
          label="Your timezone"
          hint="Your daily sending allowance rolls over at midnight here."
        >
          <Input value={tz} onChange={(event) => setTz(event.target.value)} placeholder="Asia/Jakarta" />
        </Field>

        <Field
          label="The language you read"
          hint="A draft written in another language is shown to you beside a translation into this one on Approvals. The translation is for reading only and is never sent."
        >
          <Select
            value={reading}
            onChange={(event) => setReading(event.target.value)}
            options={[
              { value: '', label: 'English, the language of this app' },
              ...OUTREACH_LANGUAGES.map((language) => ({
                value: language.code,
                label: language.name === language.endonym ? language.name : `${language.name} (${language.endonym})`,
              })),
            ]}
          />
        </Field>

        <Field
          label="Schools you went to"
          hint="One per line. When a person you write to went to one of them, a message can mention it."
        >
          <Textarea
            rows={3}
            value={schools}
            onChange={(event) => setSchools(event.target.value)}
            placeholder="University of Toronto"
          />
        </Field>
      </div>
    </Modal>
  )
}

/**
 * Read an image, fit it inside a square of `MAX_EDGE` and return a JPEG data URI.
 *
 * Cover rather than contain: a face cropped to a square is a face, and a face
 * letterboxed into a square with bars is a bad photograph. The crop is centred,
 * which is where people put themselves in a picture they chose as an avatar.
 */
async function downscale(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file)
  try {
    const edge = Math.min(MAX_EDGE, Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = edge
    canvas.height = edge
    const context = canvas.getContext('2d')
    if (context === null) throw new Error('no 2d context')

    const side = Math.min(bitmap.width, bitmap.height)
    const sx = (bitmap.width - side) / 2
    const sy = (bitmap.height - side) / 2
    context.drawImage(bitmap, sx, sy, side, side, 0, 0, edge, edge)
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY)
  } finally {
    bitmap.close()
  }
}

/** The schools typed one per line, blank lines and repeats dropped. */
export function schoolLines(text: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const line of text.split('\n')) {
    const name = line.trim()
    if (name === '' || seen.has(name.toLowerCase())) continue
    seen.add(name.toLowerCase())
    out.push(name)
  }
  return out
}
